/**
 * Loads an optional runtime brand manifest — a small JSON-shaped object
 * that lets brand values (company name, colors, logo path, feature flags)
 * be swapped without rebuilding the React bundle.
 *
 * Lookup order (first match wins):
 *   1. window.__BRAND_MANIFEST__  — a plain object a host page/shell can
 *      set on `window` before the bundle runs (e.g. inlined into
 *      index.html by a future deploy step, or injected by a domain-based
 *      loader). Synchronous, no network I/O.
 *   2. /brand-manifest.generated.json — fetched over HTTP (same-origin),
 *      produced by scripts/generate-brand-manifest.ps1 from
 *      brands/<brand>/brand.json. Only attempted when tier 1 found
 *      nothing usable.
 *   3. null — no manifest available anywhere; every consumer of this
 *      module must fail open to its existing build-time/default values.
 *
 * Two entry points:
 *   - loadRuntimeBrandManifest()      — synchronous, tier 1 only. Kept
 *     exactly as before so existing synchronous callers (getRuntimeBrandConfig())
 *     keep working unchanged.
 *   - loadRuntimeBrandManifestAsync() — async, tiers 1 then 2. Never
 *     rejects: every failure mode (HTTP error, network error, invalid
 *     JSON, invalid shape) resolves to null instead of throwing, so a
 *     bad/missing manifest file can never crash the app or need a
 *     defensive .catch() at every call site.
 *
 * Both entry points validate/sanitize whatever manifest they find via
 * validateManifestShape() before returning it: an invalid *value* for a
 * given key (e.g. a number where a string is expected) is dropped for
 * that key only — it never invalidates the whole manifest. Only a
 * completely non-object manifest is rejected outright (-> null).
 */

const MANIFEST_URL = "/brand-manifest.generated.json";
const FEATURE_FLAG_KEYS = [
  "leave",
  "payroll",
  "attendance",
  "overtime",
  "evaluations",
  "complaints",
];

function isPlainManifest(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isNonBlankString(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function sanitizeColors(candidate) {
  if (!isPlainManifest(candidate)) {
    return undefined;
  }
  const cleaned = {};
  if (isNonBlankString(candidate.primary)) {
    cleaned.primary = candidate.primary.trim();
  }
  if (isNonBlankString(candidate.secondary)) {
    cleaned.secondary = candidate.secondary.trim();
  }
  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
}

function sanitizeFeatureFlags(candidate) {
  if (!isPlainManifest(candidate)) {
    return undefined;
  }
  const cleaned = {};
  FEATURE_FLAG_KEYS.forEach((key) => {
    if (typeof candidate[key] === "boolean") {
      cleaned[key] = candidate[key];
    }
  });
  return cleaned;
}

/**
 * Sanitizes an arbitrary candidate value into a manifest-shaped object,
 * dropping any individual field that fails its own validation instead of
 * rejecting the whole manifest. Returns `null` only when the candidate
 * itself is not a plain object at all (string, array, null, undefined,
 * number, ...).
 *
 * Field rules (all fields optional — a manifest may be partial):
 *   - brandId, appName, companyName, shortName, logoPath: kept only if a
 *     non-blank string (trimmed).
 *   - colors.primary / colors.secondary: kept only if a non-blank
 *     string (trimmed); colors itself is dropped if neither survives.
 *   - featureFlags.<key>: kept only for the known feature keys and only
 *     when the value is strictly a boolean; other keys/values are
 *     dropped silently.
 */
export function validateManifestShape(candidate) {
  if (!isPlainManifest(candidate)) {
    return null;
  }

  const cleaned = {};

  if (isNonBlankString(candidate.brandId)) {
    cleaned.brandId = candidate.brandId.trim();
  }
  if (isNonBlankString(candidate.appName)) {
    cleaned.appName = candidate.appName.trim();
  }
  if (isNonBlankString(candidate.companyName)) {
    cleaned.companyName = candidate.companyName.trim();
  }
  if (isNonBlankString(candidate.shortName)) {
    cleaned.shortName = candidate.shortName.trim();
  }
  if (isNonBlankString(candidate.logoPath)) {
    cleaned.logoPath = candidate.logoPath.trim();
  }

  const colors = sanitizeColors(candidate.colors);
  if (colors) {
    cleaned.colors = colors;
  }

  const featureFlags = sanitizeFeatureFlags(candidate.featureFlags);
  if (featureFlags) {
    cleaned.featureFlags = featureFlags;
  }

  return cleaned;
}

function readWindowManifest() {
  if (typeof window === "undefined") {
    return null;
  }
  return validateManifestShape(window.__BRAND_MANIFEST__);
}

/** Synchronous tier-1-only lookup. Never performs I/O. Unchanged API. */
export function loadRuntimeBrandManifest() {
  return readWindowManifest();
}

async function fetchManifestFromNetwork() {
  if (typeof fetch !== "function") {
    return null;
  }

  let response;
  try {
    response = await fetch(MANIFEST_URL, { credentials: "same-origin" });
  } catch {
    // Network error: offline, DNS failure, CORS, aborted, etc.
    return null;
  }

  if (!response || !response.ok) {
    // HTTP error, most commonly 404 when no brand build ever ran
    // scripts/generate-brand-manifest.ps1 for this deployment.
    return null;
  }

  let json;
  try {
    json = await response.json();
  } catch {
    // Response body was not valid JSON.
    return null;
  }

  return validateManifestShape(json);
}

// In-memory cache: the network tier is fetched at most once per page
// load, no matter how many times loadRuntimeBrandManifestAsync() is
// called (e.g. from multiple BrandProvider instances in tests, or
// concurrent calls before the first one resolves) — every caller shares
// the same in-flight/resolved Promise.
let cachedManifestPromise = null;

/**
 * Async manifest loader — tier 1 (window) then tier 2 (fetch), cached.
 * Never rejects: any failure resolves to `null`.
 */
export function loadRuntimeBrandManifestAsync() {
  const fromWindow = readWindowManifest();
  if (fromWindow) {
    return Promise.resolve(fromWindow);
  }

  if (!cachedManifestPromise) {
    cachedManifestPromise = fetchManifestFromNetwork().catch(() => null);
  }
  return cachedManifestPromise;
}

/**
 * Test-only escape hatch: clears the in-memory fetch cache so each test
 * can simulate a fresh page load with its own `global.fetch` mock.
 * Production code never calls this — the whole point of the cache is
 * that a real page load only ever fetches the manifest once.
 */
export function __resetRuntimeBrandManifestCacheForTests() {
  cachedManifestPromise = null;
}

export default loadRuntimeBrandManifest;
